/**
 * © 2025 Grupo Salamander SAPI de CV
 * Author: Pablo Adoue Peralta
 *
 * Shared constants for UIButton components including default styles and configurations.
 */

import type { SxProps, Theme } from '@mui/material'
import { sizing, radii, target } from '../Theme/tokens'

/**
 * Default styling for icon-only buttons. Width is fixed to the button's own
 * height (a square icon button), set via `sx` — which always outranks the
 * theme's `MuiButton` overrides regardless of specificity, since `sx` is
 * injected last. Plan task 1.4's `@media (pointer: coarse)` height bump
 * (`Theme/overrides/buttons.ts`) therefore cannot make an icon button follow
 * along on its own; the same media query is repeated here for width so a
 * coarse pointer still gets a square `target.coarseMin` control, not a
 * 34px-wide / 44px-tall rectangle.
 */
export const ICON_BUTTON_STYLES: SxProps<Theme> = {
  paddingRight: 0,
  paddingLeft: 0,
  minWidth: sizing.buttonHeight,
  width: sizing.buttonHeight,
  borderRadius: radii.sm,
  '@media (pointer: coarse)': {
    minWidth: target.coarseMin,
    width: target.coarseMin,
  },
}

/**
 * Default button variant
 */
export const DEFAULT_VARIANT = 'contained' as const

/**
 * Default button color
 */
export const DEFAULT_COLOR = 'secondary' as const

/**
 * Default button type
 */
export const DEFAULT_TYPE = 'button' as const
